import { ViewService } from '@xeetr/types/models'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import ServiceContent from './ServiceContent'

import { ServiceFormProps } from '@/types/ServiceForm'

/**
 * Individual service form with independent FormProvider
 * Each accordion item is its own form
 */
const ServiceForm = ({
  service,
  isExpanded,
  onRegisterForm,
  onUpdate,
  onCopy,
  onDelete,
}: ServiceFormProps) => {
  const methods = useForm<ViewService>({
    defaultValues: service,
    mode: 'onChange', // Validate on change for immediate feedback
  })

  const { formState, handleSubmit, reset } = methods

  // Register/unregister form control when expanded state changes
  useEffect(() => {
    if (isExpanded) {
      const formHandle = {
        isDirty: formState.isDirty,
        isSaving: formState.isSubmitting,
        serviceId: service.id,
        onSave: handleSubmit((data) => {
          onUpdate(data)
          // Reset form with new data to mark as clean
          reset(data)
        }),
        onDiscard: () => {
          reset(service) // Reset to original service data
        },
      }
      onRegisterForm(formHandle)
    } else {
      onRegisterForm(null)
    }
  }, [isExpanded, formState.isDirty, formState.isSubmitting, service.id])

  // Update form control when dirty state changes (user types)
  useEffect(() => {
    if (isExpanded) {
      const formHandle = {
        isDirty: formState.isDirty,
        isSaving: formState.isSubmitting,
        serviceId: service.id,
        onSave: handleSubmit((data) => {
          onUpdate(data)
          reset(data)
        }),
        onDiscard: () => {
          reset(service)
        },
      }
      onRegisterForm(formHandle)
    }
  }, [formState.isDirty, formState.isSubmitting])

  // Sync form with external service changes (e.g., after successful save)
  useEffect(() => {
    reset(service)
  }, [service])

  return (
    <FormProvider {...methods}>
      <form onSubmit={(e) => e.preventDefault()}>
        <ServiceContent onCopy={onCopy} onDelete={onDelete} />
      </form>
    </FormProvider>
  )
}

export default ServiceForm
